import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { MenuInfo } from 'rc-menu/es/interface';
import { useTranslation } from 'react-i18next';
import { Select } from 'antd';

import { PageLayout, SideBarProps, TopBarProps, type AboutInfo, VERSION as ENSO_VERSION } from '@ohpen/enso-ui';
import { getCdnAssets, useConfig } from '@ohpen/react-utils';
import { useAuthz } from '@ohpen/react-authz';
import { useAuth } from '@ohpen/react-auth';

import { AppMenuItem, AppLayoutProps } from './interfaces';
import { getSideBarItems } from './sideBarItems';
import styles from './styles.module.css';
import { FeatureFlag, Permission } from '../../contexts/AuthzProvider';
import { useFeatureToggle } from '@pmp/hooks';

export const AppLayout = ({ children }: AppLayoutProps): ReactElement => {
  const config = useConfig();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const auth = useAuth();
  const { userPermissions, envFeatureFlags } = useAuthz();
  const enableDarkMode = useFeatureToggle(['enableDarkMode']);
  const { CLIENT: client, STAGE: stage, DEVELOPMENT_OVERRIDES } = config;

  const [selectedClient, setSelectedClient] = useState<string>(client);

  useEffect(() => {
    if (stage === 'dev') {
      setSelectedClient(localStorage.getItem('dev-theme') ?? client);
    }
  }, [client, stage]);

  useEffect(() => {
    if (stage === 'dev') {
      getCdnAssets({ ...(config as any), CLIENT: selectedClient });
    }
  }, [config, selectedClient, stage]);

  const logoHorizontalPath = `${config.THEMING?.ASSETS_BASE_URL ?? 'https://cdn.portal.ohpen.tech'}/${selectedClient}/${config.THEMING?.HORIZONTAL_LOGO_FILE ?? 'logo-inverted.png'}`;

  const onMenuItemClick = useCallback(
    (info: MenuInfo) => {
      navigate(`/${info.key}`);
    },
    [navigate],
  );

  const sideBarItems: AppMenuItem[] = useMemo(
    () => getSideBarItems(userPermissions as Set<Permission>, envFeatureFlags as Set<FeatureFlag>),
    [userPermissions, envFeatureFlags],
  );

  const selected = useMemo(() => {
    const key = location.pathname.split('/')[1];
    const item = sideBarItems?.find(item => item?.key === key);
    return item?.key;
  }, [location.pathname, sideBarItems]);

  const items: AppMenuItem[] = useMemo(
    () =>
      sideBarItems.map(item => (item && 'label' in item && typeof item.label === 'string' ? { ...item, label: t(item.label) } : item)) ??
      [],
    [sideBarItems, t],
  );

  const sideBar: SideBarProps = useMemo(
    () => ({
      items: items as any,
      selectedKeys: [String(selected)],
      onClick: onMenuItemClick,
      width: 220,
    }),
    [items, onMenuItemClick, selected],
  );

  const { userDisplayName: displayName } = auth;

  const onClickLogout = useCallback(async () => {
    await auth.logout();
  }, [auth]);

  const onChangeClient = useCallback((value: string) => {
    setSelectedClient(value);
    localStorage.setItem('dev-theme', value);
  }, []);

  const customMenu = useMemo(() => {
    const clientsList = DEVELOPMENT_OVERRIDES?.CLIENTS_LIST;
    if (!clientsList || stage !== 'dev') return undefined;
    const options = clientsList.map((client: string) => ({ value: client, label: client }));

    return (
      <Select className={styles.clientSelect} value={selectedClient} onChange={onChangeClient} options={options} variant="borderless" />
    );
  }, [DEVELOPMENT_OVERRIDES?.CLIENTS_LIST, onChangeClient, selectedClient, stage]);

  const aboutInfo: AboutInfo = useMemo(
    () => ({
      items: [
        { label: 'Version', value: config.VERSION || '-' },
        { label: 'Stage', value: config.STAGE || '-' },
        { label: 'Client', value: config.CLIENT || '-' },
        { label: 'Enso-UI', value: ENSO_VERSION },
      ],
    }),
    [config],
  );

  const topBar: TopBarProps = useMemo(
    () => ({
      logo: { src: logoHorizontalPath, href: '/' },
      appName: t('global.title'),
      userMenu: {
        name: displayName,
        onClickLogout,
        aboutInfo,
        showDarkModeToggle: !!enableDarkMode,
      },
      customMenu,
    }),
    [logoHorizontalPath, customMenu, onClickLogout, displayName, aboutInfo, t, enableDarkMode],
  );

  return (
    <PageLayout sideBar={sideBar} topBar={topBar}>
      {children}
    </PageLayout>
  );
};
